import { supabase } from './supabase';
import { formatLocalYmd, isValidArchiveDate, parseYmd } from './dates';

export type ArchiveIndexTopic = {
  title: string;
  play_date: string;
};

const CACHE_MS = 60 * 60 * 1000;
let cached: { at: number; topics: ArchiveIndexTopic[] } | null = null;
let inflight: Promise<ArchiveIndexTopic[]> | null = null;

export function slugFromTitle(title: string) {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96);
  return slug || 'topic';
}

export function archiveSlug(topic: ArchiveIndexTopic, siblings: ArchiveIndexTopic[]) {
  const base = slugFromTitle(topic.title);
  const clash = siblings.some(
    (other) => other.play_date !== topic.play_date && slugFromTitle(other.title) === base
  );
  return clash ? `${base}-${topic.play_date}` : base;
}

export function archivePath(topic: ArchiveIndexTopic, siblings: ArchiveIndexTopic[] = cached?.topics ?? []) {
  return `/archive/${archiveSlug(topic, siblings)}`;
}

async function loadIndex(): Promise<ArchiveIndexTopic[]> {
  const today = formatLocalYmd();
  const { data, error } = await supabase
    .from('topics')
    .select('title, play_date')
    .lt('play_date', today)
    .order('play_date', { ascending: false });
  if (error || !data) return [];
  return data as ArchiveIndexTopic[];
}

export async function fetchArchiveIndex(): Promise<ArchiveIndexTopic[]> {
  if (cached && Date.now() - cached.at < CACHE_MS) return cached.topics;
  if (inflight) return inflight;

  inflight = loadIndex()
    .then((topics) => {
      cached = { at: Date.now(), topics };
      return topics;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export function findArchiveTopic(param: string, topics: ArchiveIndexTopic[]) {
  const asDate = parseYmd(param);
  if (asDate) {
    return topics.find((t) => t.play_date === asDate) ?? null;
  }
  return topics.find((t) => archiveSlug(t, topics) === param) ?? null;
}

export async function resolveArchiveParam(param: string) {
  const topics = await fetchArchiveIndex();
  const topic = findArchiveTopic(param, topics);
  if (!topic || !isValidArchiveDate(topic.play_date)) return null;
  return {
    topic,
    slug: archiveSlug(topic, topics),
    path: archivePath(topic, topics),
    topics,
  };
}
